import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import styles from '@shared/styles/components/NoticeBanner.module.css'
import { SystemIcon, SystemIconType } from '@shared/designSystem/SystemIcon'

export type NoticeLevel = 'critical' | 'warning' | 'communication'

export interface NoticeBannerLabels {
    /** Visually hidden level prefix read before the title ("Aviso crítico"). */
    level: string
    more: string
    less: string
    /** Accessible name of the dismiss control, already including the title. */
    dismiss: string
}

export interface NoticeBannerCTA {
    label: string
    href: string
}

export interface NoticeBannerProps {
    level: NoticeLevel
    title: string
    /** Basic markdown: bold, italic, links, bullet lists and line breaks. */
    body: string
    labels: NoticeBannerLabels
    /** Small cues rendered after the title (scope, "Actualizado"). */
    meta?: string[]
    cta?: NoticeBannerCTA
    /** Omitted → no dismiss control (critical notices). */
    onDismiss?: () => void
    /** Fired for the CTA and for links inside the body. */
    onLinkClick?: (href: string) => void
    /** bar: flush row inside the shell bar; card: standalone rounded card. */
    variant?: 'bar' | 'card'
    /** compact: one-line title, body and CTA only when expanded. */
    density?: 'full' | 'compact'
    defaultExpanded?: boolean
    /** false: always shows the full body with no Ver más / Ver menos (popover cards). */
    collapsible?: boolean
    /** Extra controls rendered at the end of the action cluster (e.g. "+N avisos"). */
    trailing?: React.ReactNode
    className?: string
}

const LEVEL_ICON: Record<NoticeLevel, SystemIconType> = {
    critical: 'warning-triangle',
    warning: 'clock',
    communication: 'info',
}

export const isExternalHref = (href: string): boolean => /^https?:\/\//i.test(href)

/** Markdown → a single line of plain text for the collapsed, clamped row. */
export const flattenNoticeMarkdown = (markdown: string): string => {
    return markdown
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/(\*\*|__)(.*?)\1/g, '$2')
        .replace(/(\*|_)(.*?)\1/g, '$2')
        .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '• ')
        .replace(/\s*\n+\s*/g, ' ')
        .trim()
}

/** Single newlines become hard breaks, except before list items. */
const withHardBreaks = (markdown: string): string =>
    markdown.replace(/([^\n])\n(?!\n|\s*(?:[-*+]|\d+\.)\s)/g, '$1  \n')

const hasStructure = (markdown: string): boolean =>
    /\n/.test(markdown.trim()) || /^\s*(?:[-*+]|\d+\.)\s/m.test(markdown)

const ALLOWED_ELEMENTS = ['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']

const NoticeLink: React.FC<{ href?: string, children?: React.ReactNode, onLinkClick?: (href: string) => void, className?: string }> = ({ href, children, onLinkClick, className }) => {
    if (!href) {
        return <>{children}</>
    }
    const external = isExternalHref(href)
    return (
        <a
            href={href}
            className={className}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            onClick={() => onLinkClick?.(href)}
        >
            {children}
        </a>
    )
}

export const NoticeMarkdown: React.FC<{ markdown: string, onLinkClick?: (href: string) => void }> = ({ markdown, onLinkClick }) => (
    <ReactMarkdown
        allowedElements={ALLOWED_ELEMENTS}
        unwrapDisallowed={true}
        components={{
            a: ({ href, children }) => (
                <NoticeLink href={href} onLinkClick={onLinkClick} className={styles.link}>{children}</NoticeLink>
            ),
        }}
    >
        {withHardBreaks(markdown)}
    </ReactMarkdown>
)

/** Untruncated body (and optional CTA) for surfaces with room, e.g. the acknowledge modal. */
export const NoticeFullBody: React.FC<{ body: string, cta?: NoticeBannerCTA, onLinkClick?: (href: string) => void }> = ({ body, cta, onLinkClick }) => (
    <div className={['DSNoticeFullBody', styles.fullBody].join(' ')}>
        <NoticeMarkdown markdown={body} onLinkClick={onLinkClick} />
        {cta ? <NoticeLink href={cta.href} onLinkClick={onLinkClick} className={styles.cta}>{cta.label}</NoticeLink> : null}
    </div>
)

export const NoticeBanner: React.FC<NoticeBannerProps> = ({
    level,
    title,
    body,
    labels,
    meta = [],
    cta,
    onDismiss,
    onLinkClick,
    variant = 'card',
    density = 'full',
    defaultExpanded = false,
    collapsible = true,
    trailing,
    className,
}) => {
    const [expandedState, setExpanded] = useState(defaultExpanded)
    const expanded = expandedState || !collapsible
    const [overflows, setOverflows] = useState(false)
    const clampRef = useRef<HTMLDivElement>(null)
    const plainBody = flattenNoticeMarkdown(body)
    const compact = density === 'compact'

    useLayoutEffect(() => {
        const element = clampRef.current
        if (!element || expanded || compact) {
            return
        }
        const measure = () => setOverflows(element.scrollHeight > element.clientHeight + 1)
        measure()
        if (typeof ResizeObserver === 'undefined') {
            return
        }
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [expanded, compact, plainBody, title])

    const canToggle = collapsible && plainBody.length > 0 && (compact || overflows || hasStructure(body))
    // Cards have the room to put the CTA under the body; the bar keeps it in the action cluster.
    const ctaBelowBody = compact || variant === 'card'
    const showBodyInline = expanded || !compact
    const toggle = useCallback(() => setExpanded((value) => !value), [])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && expanded) {
            event.stopPropagation()
            setExpanded(false)
        }
    }

    const titleLine = (
        <>
            <span className={styles.srOnly}>{labels.level}: </span>
            <span className={styles.title}>{title}</span>
            {meta.map((cue) => (
                <span key={cue} className={styles.meta}>{cue}</span>
            ))}
        </>
    )

    return (
        <div
            role={level === 'critical' ? 'alert' : 'status'}
            data-level={level}
            data-expanded={expanded ? 'true' : undefined}
            className={[
                styles.notice,
                variant === 'bar' ? styles.bar : styles.card,
                compact ? styles.compact : '',
                'DSNoticeBanner',
                className,
            ].filter(Boolean).join(' ')}
            onKeyDown={handleKeyDown}
        >
            <span className={styles.icon} aria-hidden="true">
                <SystemIcon type={LEVEL_ICON[level]} color="currentColor" />
            </span>
            <div className={styles.content}>
                {expanded ? (
                    <>
                        <div>{titleLine}</div>
                        <div className={styles.body}>
                            <NoticeMarkdown markdown={body} onLinkClick={onLinkClick} />
                        </div>
                        {ctaBelowBody && cta ? (
                            <NoticeLink href={cta.href} onLinkClick={onLinkClick} className={styles.cta}>{cta.label}</NoticeLink>
                        ) : null}
                    </>
                ) : (
                    <div ref={clampRef} className={compact ? styles.singleLine : styles.clamp}>
                        {titleLine}
                        {showBodyInline && plainBody ? <span className={styles.plain}>{plainBody}</span> : null}
                    </div>
                )}
            </div>
            <div className={styles.actions}>
                {cta && !(ctaBelowBody && expanded) && !compact ? (
                    <NoticeLink href={cta.href} onLinkClick={onLinkClick} className={styles.cta}>{cta.label}</NoticeLink>
                ) : null}
                {canToggle ? (
                    <button type="button" className={styles.control} aria-expanded={expanded} onClick={toggle}>
                        {expanded ? labels.less : labels.more}
                    </button>
                ) : null}
                {trailing}
                {onDismiss ? (
                    <button
                        type="button"
                        className={styles.dismiss}
                        aria-label={labels.dismiss}
                        title={labels.dismiss}
                        onClick={onDismiss}
                    >
                        <SystemIcon type="close" color="currentColor" />
                    </button>
                ) : null}
            </div>
        </div>
    )
}

export default NoticeBanner
